import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { money, GENDER_PREF } from "@/data/constants";
import { PaywallModal } from "@/components/PaywallModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MapPin, Users, GraduationCap, Lock, Phone, Mail, User, Loader2, Check } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

export default function RoommateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [contact, setContact] = useState(null);
  const [paywall, setPaywall] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const unlocked = JSON.parse(localStorage.getItem("rento_unlocked") || "{}");
      const ref = unlocked[id];
      const { data } = await api.get(`/roommates/${id}`, { params: ref ? { payment_ref: ref } : {} });
      setPost(data);
      if (data.contact) setContact(data.contact);
    } catch { setPost(false); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#2C3830]" /></div>;
  if (!post) return <div className="mx-auto max-w-3xl px-5 py-24 text-center"><p className="font-display text-2xl text-[#2C3830]">Post not found</p><Button onClick={() => navigate("/students")} className="mt-6 rounded-full bg-[#2C3830]">Back to students</Button></div>;

  const photos = post.photos?.length ? post.photos : [];
  const cover = photos.length ? fileUrl(photos[active]) : FALLBACK;
  const gender = GENDER_PREF.find((g) => g.value === post.gender_pref);

  const onUnlocked = (c, ref) => {
    setContact(c);
    const u = JSON.parse(localStorage.getItem("rento_unlocked") || "{}"); u[id] = ref; localStorage.setItem("rento_unlocked", JSON.stringify(u));
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
      <button data-testid="rm-back" onClick={() => navigate(-1)} className="mb-6 flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#2C3830]"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border/60 bg-[#EDE9E1]">
            <img src={cover} alt={post.title} className="h-full w-full object-cover" onError={(e) => (e.currentTarget.src = FALLBACK)} />
            <Badge className="absolute left-4 top-4 rounded-full border-0 bg-[#D4A373] text-white">{post.post_type === "seeking" ? "Looking for a room" : "Roommate wanted"}</Badge>
          </div>
          {photos.length > 1 && (
            <div className="mt-3 flex gap-3 overflow-x-auto rento-scroll-hide pb-1">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setActive(i)} className={`h-20 w-24 shrink-0 overflow-hidden rounded-xl border-2 ${active === i ? "border-[#D4A373]" : "border-transparent opacity-70"}`}><img src={fileUrl(p)} alt="" className="h-full w-full object-cover" /></button>
              ))}
            </div>
          )}
          {post.description && (<div className="mt-6"><h2 className="font-display text-xl font-medium text-[#2C3830]">About the space</h2><p className="mt-2 whitespace-pre-line leading-relaxed text-[#6B7280]">{post.description}</p></div>)}
          {post.amenities?.length > 0 && (
            <div className="mt-6"><h2 className="font-display text-xl font-medium text-[#2C3830]">Amenities</h2>
              <div className="mt-3 flex flex-wrap gap-2">{post.amenities.map((a) => <span key={a} className="flex items-center gap-1 rounded-full bg-[#F7F5F0] px-3 py-1.5 text-sm text-[#2C3830]"><Check className="h-3.5 w-3.5 text-[#D4A373]" /> {a}</span>)}</div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <h1 className="font-display text-3xl font-medium leading-tight text-[#1A1A1A]">{post.title}</h1>
          {post.institution && <div className="mt-2 flex items-center gap-1.5 text-[#6B7280]"><GraduationCap className="h-4 w-4 text-[#D4A373]" /> {post.institution}</div>}
          <div className="mt-2 flex items-center gap-1.5 text-[#6B7280]"><MapPin className="h-4 w-4 text-[#D4A373]" /> {post.estate ? `${post.estate}, ` : ""}{post.town}, {post.county}</div>
          <div className="mt-5 flex items-end gap-1"><span className="font-display text-4xl font-semibold text-[#2C3830]">{money(post.rent_share, post.currency)}</span><span className="pb-1 text-[#6B7280]">{post.post_type === "seeking" ? "budget" : "/mo share"}</span></div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            {post.current_roommates != null && <div className="flex items-center gap-2 rounded-xl bg-white p-3 text-sm"><Users className="h-4 w-4 text-[#D4A373]" /><span className="text-[#6B7280]">Current</span><span className="ml-auto font-medium text-[#2C3830]">{post.current_roommates}</span></div>}
            {post.spots_available != null && <div className="flex items-center gap-2 rounded-xl bg-white p-3 text-sm"><Users className="h-4 w-4 text-[#D4A373]" /><span className="text-[#6B7280]">Spots</span><span className="ml-auto font-medium text-[#2C3830]">{post.spots_available}</span></div>}
            {gender && <div className="col-span-2 flex items-center gap-2 rounded-xl bg-white p-3 text-sm"><User className="h-4 w-4 text-[#D4A373]" /><span className="text-[#6B7280]">Preference</span><span className="ml-auto font-medium text-[#2C3830]">{gender.label}</span></div>}
          </div>

          <div className="relative mt-6 overflow-hidden rounded-2xl border border-border/60 bg-white p-5">
            <h3 className="font-display text-lg font-medium text-[#2C3830]">Poster contact</h3>
            {contact ? (
              <div data-testid="rm-contact-revealed" className="mt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-[#1A1A1A]"><User className="h-4 w-4 text-[#D4A373]" /> {contact.name}</div>
                <a href={`tel:${contact.phone}`} data-testid="rm-contact-phone" className="flex items-center gap-2 text-sm font-medium text-[#2C3830]"><Phone className="h-4 w-4 text-[#D4A373]" /> {contact.phone}</a>
                {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-[#2C3830]"><Mail className="h-4 w-4 text-[#D4A373]" /> {contact.email}</a>}
              </div>
            ) : (
              <>
                <div className="mt-4 space-y-3 blur-sm select-none" aria-hidden><div className="flex items-center gap-2 text-sm"><User className="h-4 w-4" /> Hidden Name</div><div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4" /> 07XX XXX XXX</div></div>
                <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 bg-white/70 p-5 backdrop-blur-xl">
                  <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Lock className="h-4 w-4" /> Unlock for {money(post.unlock_fee, post.currency)}</div>
                  <Button data-testid="rm-unlock-btn" onClick={() => setPaywall(true)} className="w-full rounded-full bg-[#25D366] py-5 font-semibold text-white hover:bg-[#1fb85a]">Pay with M-Pesa to reveal contact</Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <PaywallModal open={paywall} onOpenChange={setPaywall} listing={post} payPath={`/roommates/${post.id}/pay`} onUnlocked={onUnlocked} />
    </div>
  );
}
